import React, { useContext, useMemo } from "react";
import { Box, Checkbox, Collapse, Typography } from "@mui/material";
import { Arrow } from "../Arrow/Arrow";
import { PlanningGridContext } from "../PlanningGrid";
import { ActionsMenu } from "../../../../components/ActionsMenu/ActionsMenu";
import DownloadIcon from "../../../../components/VectorIcons/DownloadIcon";
import ReleaseTextIcon from "../../../../components/VectorIcons/ReleaseTextIcon";
import ReleaseTextIconMarked from "../../../../components/VectorIcons/ReleaseTextIconMarked";
import DeleteIcons from "../../../ProjectSheet/ListFiles/BlockList/DeleteIcons/DeleteIcons";
import DeleteTooltip from "../../../ProjectSheet/ListFiles/DeleteTooltip/DeleteTooltip";
import PencilEditIcon from "../../../../components/VectorIcons/PencilEditIcon";
import useDownloadAttachment from "../../../../hooks/useDownloadAttachment";
import ProjectSheet from "../../../../context/sheetContext";
import UserContext from "../../../../context/userContext";
import {
  formatDate,
  getReleaseDestinationLabel,
} from "../../../../utils";
import {
  toggleOneItem,
  toggleAllItems,
  areAllSelected,
  areSomeSelected,
  extractAttachmentDates,
  buildFileDownloadObject,
  createSelectableAddendaList,
  createSelectableAddendumItem,
} from "./addendumSection.utils";
import { getLatestReleaseComment } from "../../utils";
import { ADDENDUM_GRID_LAYOUT, ADDENDUM_DATE_COL_WIDTH } from "../configs";
import CustomTooltip from "../../../../components/CustomTooltip/CustomTooltip";

export const AddendumSection = ({
  id,
  addenda = [],
  label,
  scope = "chapter",
  attachmentActions,
  handleDeleteAddendum,
  handleEditAddendum,
  handleOpenReleaseTexts,
  selectedFiles = [],
  setSelectedFiles = () => {},
}) => {
  const hasAddenda = !!addenda?.length;

  const { isSelectionMode, openIds, toggleOpenId } =
    useContext(PlanningGridContext);
  const { downloadAttachment } = useDownloadAttachment();
  const projectSheet = useContext(ProjectSheet);
  const userContext = useContext(UserContext);
  const {
    canDelete,
    getDeleteUnavailableMessage,
    canDownload,
    canReleaseChannel,
    canUnReleaseChannel,
    getReleaseUnavailableMessage,
    getUnReleaseUnavailableMessage,
  } = attachmentActions;

  const hasReleasePermission = userContext.hasPermission({
    required: ["AttachmentRelease"],
  });

  const open = isSelectionMode || openIds.includes(id);

  const selectableItems = useMemo(
    () => createSelectableAddendaList(addenda, scope === "chapter"),
    [addenda, scope]
  );

  const selectedIds = useMemo(
    () => new Set((selectedFiles || []).map((file) => file.id)),
    [selectedFiles]
  );

  const isItemSelected = (itemId) => selectedIds.has(itemId);

  const toggleOne = (item) => {
    setSelectedFiles((prev) => toggleOneItem(prev, item));
  };

  const toggleAll = () => {
    setSelectedFiles((prev) => toggleAllItems(prev, selectableItems));
  };

  const allSelected = areAllSelected(selectableItems, selectedIds);
  const someSelected = areSomeSelected(
    selectableItems,
    selectedIds,
    allSelected
  );

  const addendumGridTemplate = isSelectionMode
    ? `48px ${ADDENDUM_GRID_LAYOUT}`
    : ADDENDUM_GRID_LAYOUT;

  if (!hasAddenda) return null;

  return (
    <Box>
      {/* Section Header */}
      <Box
        display="grid"
        gridTemplateColumns={addendumGridTemplate}
        sx={{
          bgcolor: open ? "#F6F6FE" : "#ffffff",
          borderBottom: open ? "1px solid transparent" : "1px solid #e0e2ff",
          minHeight: "44px",
          pl: "16px",
          pr: "16px",
        }}
      >
        {isSelectionMode && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
              sx={{
                color: "#43444D",
                "&.Mui-checked": { color: "#43444D" },
                "&.MuiCheckbox-indeterminate": { color: "#43444D" },
              }}
            />
          </Box>
        )}
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: isSelectionMode ? "default" : "pointer" }}
          onClick={() => !isSelectionMode && toggleOpenId(id)}
        >
          {!isSelectionMode && <Arrow isOpen={open} margin="0 14px 0 16px" />}
          <Typography
            fontWeight="bold"
            sx={{
              fontFamily: "inherit",
              fontSize: "14px",
              color: "#1E9F69",
              userSelect: "none",
            }}
          >
            {label}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center">
          {open && (
            <>
              <Box sx={{ width: ADDENDUM_DATE_COL_WIDTH, textAlign: "center" }}>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#58627D",
                  }}
                >
                  Entregue
                </Typography>
              </Box>
              <Box sx={{ width: ADDENDUM_DATE_COL_WIDTH, textAlign: "center" }}>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#58627D",
                  }}
                >
                  Liberado
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Box />
      </Box>

      <Collapse in={open} timeout={200} unmountOnExit>
        <Box sx={{ pl: "16px", pr: "16px", pb: "8px", bgcolor: "#F6F6FE" }}>
          {addenda.map((addendum) => {
            const attachment = addendum.attachment;
            const attachmentId = attachment.id;

            const isDeletable = canDelete(attachmentId);
            const deleteUnavailableMessage =
              getDeleteUnavailableMessage(attachmentId);
            const canDownloadThis = canDownload(attachmentId);

            const { deliveryDate, releaseDate } =
              extractAttachmentDates(attachment);
            const fileToDownload = buildFileDownloadObject(
              attachment,
              projectSheet?.list?.result?.id
            );
            const lastReleaseComment = getLatestReleaseComment(
              attachment.releaseManagement
            );

            const releaseDestinationLabel = getReleaseDestinationLabel({
              channel: Boolean(attachment.releaseManagement?.channel),
              production: Boolean(attachment.releaseManagement?.production),
              cultureHub: Boolean(attachment.releaseManagement?.cultureHub),
            });

            const isReleased = Boolean(releaseDestinationLabel);

            const canReleaseThis = isReleased
              ? canUnReleaseChannel(attachmentId)
              : canReleaseChannel(attachmentId);

            const releaseUnavailableMessage = isReleased
              ? getUnReleaseUnavailableMessage(attachmentId)
              : getReleaseUnavailableMessage(attachmentId);

            return (
              <Box
                key={addendum.id}
                display="grid"
                gridTemplateColumns={addendumGridTemplate}
                sx={{
                  minHeight: "44px",
                  bgcolor: "#EAECF7",
                  borderBottom: "1px solid #FFF",
                  pr: "6px",
                }}
              >
                {isSelectionMode && (
                  <Checkbox
                    size="small"
                    checked={isItemSelected(attachment.id)}
                    onChange={() =>
                      toggleOne(
                        createSelectableAddendumItem(
                          addendum,
                          scope === "chapter"
                        )
                      )
                    }
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: "#43444D",
                      "&.Mui-checked": { color: "#43444D" },
                    }}
                  />
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  pl={isSelectionMode ? "0" : "16px"}
                  sx={{ pt: "6px", pb: "6px", pr: "12px" }}
                >
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "14px",
                      color: "#7F808E",
                      whiteSpace: "nowrap",
                      pr: "32px",
                    }}
                  >
                    {attachment.type?.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "14px",
                      color: "#7F808E",
                      wordBreak: "break-word",
                    }}
                  >
                    {attachment.fileName}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: ADDENDUM_DATE_COL_WIDTH,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "12px", fontFamily: "inherit" }}
                    >
                      {formatDate(deliveryDate, "-")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: ADDENDUM_DATE_COL_WIDTH,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                        fontFamily: "inherit",
                        position: "relative",
                      }}
                    >
                      {formatDate(releaseDate, "-")}

                      {releaseDestinationLabel && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "-5px",
                            right: "-32px",
                          }}
                        >
                          <CustomTooltip
                            dataTip={`Liberado para ${releaseDestinationLabel}`}
                            tipLabel={
                              <div className="tooltipIcon">
                                <ReleaseTextIconMarked />
                              </div>
                            }
                            theme="light"
                            position="bottom"
                          />
                        </Box>
                      )}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flex: "1",
                  }}
                >
                  <ActionsMenu
                    disabled={isSelectionMode}
                    menuOptions={[
                      {
                        action: () => downloadAttachment(fileToDownload),
                        label: "Baixar",
                        disabled: !canDownloadThis,
                        icon: <DownloadIcon color="#8C8EF5" />,
                      },
                      {
                        action: () =>
                          handleOpenReleaseTexts(
                            {
                              ...attachment,
                              releaseType: "Channel",
                              observation: lastReleaseComment,
                            },
                            addenda
                              .filter((a) => a.id !== addendum.id)
                              .map((a) => ({
                                ...a.attachment,
                                releaseType: "Channel",
                              })),
                            { type: "planningAddendum" }
                          ),
                        disabled: !hasReleasePermission || !canReleaseThis,
                        tooltip: releaseUnavailableMessage ? (
                          <DeleteTooltip message={releaseUnavailableMessage} />
                        ) : null,
                        label: "Liberação",
                        icon: releaseDestinationLabel ? (
                          <ReleaseTextIconMarked />
                        ) : (
                          <ReleaseTextIcon />
                        ),
                      },
                      {
                        action: () => handleEditAddendum(addendum),
                        label: "Editar",
                        icon: <PencilEditIcon />,
                      },
                      {
                        action: () => handleDeleteAddendum(addendum, scope),
                        label: "Remover",
                        disabled: !isDeletable,
                        tooltip: deleteUnavailableMessage ? (
                          <DeleteTooltip message={deleteUnavailableMessage} />
                        ) : null,
                        icon: (
                          <DeleteIcons
                            returnColor={() => (isDeletable ? "#000" : "#CCC")}
                          />
                        ),
                      },
                    ]}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};
